import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import { useBgReport, useBgJob } from './BackgroundJobs';
import CoperturaMatrice from './CoperturaMatrice';

// Verifica dominio singola via /dominio/verifica (456 + Copertura + Lucchetto). Estetica fumé.
const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

const STEP_LABEL = { n456: '456', copertura: 'Copertura', lucchetto: 'Lucchetto' };
const STEPS = ['n456', 'copertura', 'lucchetto'];
const finito = (s) => !!(s && s.stato && s.stato !== 'in_corso' && s.stato !== 'attesa');
// Esito compatto di uno step (456/Lucchetto) o matrice (Copertura). Gestisce saltato/errore.
function StepBadge({ s, kind }) {
  if (!s) return <span style={{ color: 'rgba(255,255,255,.4)', fontSize: 12.5 }}>—</span>;
  if (s.stato === 'in_corso' || s.stato === 'attesa') return <span style={{ color: '#fbbf24', fontSize: 12.5 }}>in corso…</span>;
  if (s.stato === 'saltato') return <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 12.5 }}>saltato{s.motivo ? ` · ${s.motivo}` : ''}</span>;
  if (s.stato === 'errore') return <span style={{ color: '#fca5a5', fontSize: 12.5 }}>errore{s.motivo ? ` · ${s.motivo}` : ''}</span>;
  if (kind === 'copertura') {
    // Ripiego: SalesMate non disponibile, la copertura l'ha data il sito TIM. Va detto,
    // perche' e' un'altra fonte e non ha la matrice per servizio (18/09/2026).
    if (s.fonte) {
      return (
        <span style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                         color: '#0b1a10', background: /^COPERTO/i.test(s.esito || '') ? '#22c55e' : '#94a3b8' }}>
            {s.esito || '—'}
          </span>
          <span style={{ color: 'rgba(255,255,255,.55)' }} title={s.motivo || ''}>via {s.fonte}</span>
        </span>
      );
    }
    return <CoperturaMatrice d={s} />;
  }
  const e = (s.esito || '').toUpperCase();
  const col = kind === 'n456' ? (e === 'NON TIM' ? '#22c55e' : e === 'TIM' ? '#e11d48' : '#f59e0b')
    : (e === 'VERDE' ? '#22c55e' : e === 'GRIGIO' ? '#9ca3af' : '#64748b');
  const lbl = kind === 'lucchetto' ? (e === 'VERDE' ? 'Vendibile' : e === 'GRIGIO' ? 'Presidiato' : (s.esito || '—')) : (s.esito || '—');
  return <span style={{ fontSize: 13, fontWeight: 700, padding: '3px 10px', borderRadius: 999, color: '#0b1a10', background: col }}>{lbl}</span>;
}
const rowLbl = { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,.5)' };

export default function DominioDialog({ open, onClose }) {
  const [dominio, setDominio] = useState('');
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);

  const clear = () => { clearInterval(poll.current); setDominio(''); setBusy(false); setJob(null); setJobId(null); };

  const errore = !!job && !!job.errore;
  const summary = job ? (errore ? 'errore' : 'completato') : null;
  useBgReport('dominio', { label: 'Verifica dominio', color: '#fb923c', iconKey: 'dominio' },
    { open, running: busy, done: !busy && !!job && !errore, error: errore, summary, jobId, active: busy || !!job });

  const entry = useBgJob('dominio');
  const had = useRef(false);
  useEffect(() => { if (entry) had.current = true; else if (had.current) { had.current = false; clear(); } }, [entry]); // eslint-disable-line
  // riaprendo la notifica: se non ho il risultato in locale, lo ripesco dal server col jobId
  useEffect(() => {
    if (open && !busy && !job && entry && entry.jobId) {
      axios.get(`${API}/dominio/verifica/${entry.jobId}`).then((r) => setJob(r.data)).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const avvia = async () => {
    const d = dominio.trim();
    if (!d) return;
    clearInterval(poll.current);
    setBusy(true); setJob(null);
    try {
      const r = await axios.post(`${API}/dominio/verifica`, { dominio: d });
      const id = r.data.job_id; setJobId(id);
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/dominio/verifica/${id}`);
          setJob(s.data);
          if (s.data.stato === 'completato' || s.data.stato === 'errore') { clearInterval(poll.current); setBusy(false); }
        } catch { /* ritenta */ }
      }, 1500);
    } catch (e) { setBusy(false); setJob({ stato: 'errore', errore: e.response?.data?.detail || 'Errore avvio' }); }
  };

  const dd = job?.dati || {};
  const step = job?.step || {};
  const fatti = STEPS.filter((k) => finito(step[k])).length;
  const pct = job?.stato === 'completato' ? 100 : Math.max(8, Math.round((fatti / STEPS.length) * 100));

  return createPortal(
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-dialog dom-dlg" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-content">
          <div className="bulk-head" style={{ marginBottom: 14 }}>
            <span className="bulk-title">Verifica dominio</span>
            <button className="bulk-x" onClick={onClose}><IconClose /></button>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input value={dominio} onChange={(e) => setDominio(e.target.value)} placeholder="es. azienda.it"
              onKeyDown={(e) => { if (e.key === 'Enter') avvia(); }} disabled={busy}
              style={{ flex: 1, minWidth: 0, padding: '9px 12px', borderRadius: 9, border: '1px solid rgba(255,255,255,.14)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 13 }} />
            <button onClick={avvia} disabled={busy || !dominio.trim()}
              style={{ padding: '9px 16px', borderRadius: 9, border: 0, fontWeight: 800, fontSize: 13, cursor: busy || !dominio.trim() ? 'default' : 'pointer', opacity: busy || !dominio.trim() ? 0.6 : 1, color: '#fff', background: 'linear-gradient(180deg,#fb923c,#ea580c)' }}>
              {busy ? '…' : 'Verifica'}
            </button>
          </div>

          {busy ? (
            <div style={{ padding: '4px 0 6px' }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', marginBottom: 8 }}>Analisi in corso…</div>
              <div className="indaga-track"><div className="indaga-fill" style={{ width: `${pct}%` }} /></div>
            </div>
          ) : job ? (
            job.errore ? <div style={{ color: '#fca5a5', fontSize: 13 }}>{job.errore}</div> : (
              <div>
                {(dd.ragione_sociale || dd.partita_iva) && (
                  <div style={{ fontWeight: 800, fontSize: 15, color: '#fff', marginBottom: 4 }}>{dd.ragione_sociale || dd.partita_iva}</div>
                )}
                {(dd.partita_iva || dd.telefono || dd.indirizzo) && (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginBottom: 10 }}>
                    {dd.partita_iva && <span><span style={{ color: 'rgba(255,255,255,.45)' }}>P.IVA</span> {dd.partita_iva}</span>}
                    {dd.telefono && <span><span style={{ color: 'rgba(255,255,255,.45)' }}>Tel</span> {dd.telefono}</span>}
                    {dd.indirizzo && <span><span style={{ color: 'rgba(255,255,255,.45)' }}>Ind.</span> {dd.indirizzo}</span>}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={rowLbl}>{STEP_LABEL.n456}</span><StepBadge s={step.n456} kind="n456" /></div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={rowLbl}>{STEP_LABEL.lucchetto}</span><StepBadge s={step.lucchetto} kind="lucchetto" /></div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,.5)', marginBottom: 6 }}>{STEP_LABEL.copertura}</div>
                    <StepBadge s={step.copertura} kind="copertura" />
                  </div>
                </div>
              </div>
            )
          ) : entry && entry.status !== 'running' ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', padding: '8px 0' }}>Ultimo esito: <b style={{ color: '#fff' }}>{entry.summary || '—'}</b> <span style={{ color: 'rgba(255,255,255,.45)' }}>(fai una nuova verifica sopra)</span></div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '8px 0' }}>
              Inserisci un dominio: recupero P.IVA, indirizzo e telefono dal sito, poi lancio 456, Copertura e Lucchetto.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
